import './style.css';
import { renderAudioArchive, renderAudioBriefingCard, renderFeed, renderLoadingState } from './js/render.js';
import { closeAgentProfile, closeSharePreview } from './js/interactions.js';
import { appendPosts, posts, setPosts } from './js/data.js';
import { fetchAudioEpisodes, fetchFeed, fetchLatestAudioEpisode, fetchPost } from './js/api.js';
import { getViewerId } from './js/utils.js';

const POLL_INTERVAL_MS = 15 * 60 * 1000;
const PAGE_SIZE = 20;
const viewerId = getViewerId();
let feedOffset = 0;
let hasMore = false;
let isLoadingMore = false;
let archiveEpisodes = [];
let selectedArchiveEpisodeId = null;

function setSyncStatus(message, isError = false) {
  const status = document.getElementById('syncStatus');
  if (!status) return;
  status.innerHTML = message;
  status.classList.toggle('error', isError);
}

function parseRoute() {
  const hash = window.location.hash || '';
  const briefingMatch = hash.match(/^#\/briefings(?:\/(.+))?$/);
  if (briefingMatch) {
    return { view: 'briefings', episodeId: briefingMatch[1] ? decodeURIComponent(briefingMatch[1]) : null };
  }
  const match = hash.match(/^#\/post\/(.+)$/);
  if (match) {
    return { view: 'post', postId: decodeURIComponent(match[1]) };
  }
  return { view: 'feed' };
}

async function refreshFeed() {
  const feed = document.getElementById('feed');
  if (feed && posts.length === 0) {
    renderLoadingState(feed);
  }

  setSyncStatus('// SYNCING FEED...');
  try {
    const result = await fetchFeed(PAGE_SIZE, 0, viewerId);
    setPosts(result.items);
    feedOffset = result.items.length;
    hasMore = result.hasMore;
    if (feed) {
      renderFeed(feed, posts);
    }
    updateLoadMoreButton();
    setSyncStatus(`// LIVE // ${new Date().toLocaleTimeString()}`);
  } catch (error) {
    console.error('Failed to load feed:', error);
    setSyncStatus('// BACKEND OFFLINE OR ERROR', true);
  }
}

async function refreshAudioBriefing() {
  const container = document.getElementById('audioBriefing');
  if (!container) return;
  try {
    const episode = await fetchLatestAudioEpisode();
    renderAudioBriefingCard(container, episode);
  } catch (error) {
    console.error('Failed to load latest audio briefing:', error);
    container.innerHTML = '';
  }
}

async function loadAudioArchive() {
  const feed = document.getElementById('feed');
  if (!feed) return;
  renderLoadingState(feed);
  setSyncStatus('// LOADING BRIEFINGS...');
  try {
    archiveEpisodes = await fetchAudioEpisodes(30);
    const route = parseRoute();
    const preferredId = selectedArchiveEpisodeId || route.episodeId || null;
    const resolvedId = archiveEpisodes.some((episode) => episode.id === preferredId)
      ? preferredId
      : (archiveEpisodes[0]?.id || null);
    selectedArchiveEpisodeId = resolvedId;
    renderAudioArchive(feed, archiveEpisodes, resolvedId);
    setSyncStatus('// AUDIO ARCHIVE // <a href="/" style="color: var(--pink); font-weight: 700;">← BACK TO FEED</a>');
  } catch (error) {
    console.error('Failed to load audio archive:', error);
    setSyncStatus('// AUDIO ARCHIVE ERROR', true);
  }
}

function selectAudioEpisode(episodeId) {
  if (!episodeId || !archiveEpisodes.length) return;
  if (!archiveEpisodes.some((episode) => episode.id === episodeId)) return;
  selectedArchiveEpisodeId = episodeId;
  const feed = document.getElementById('feed');
  if (!feed) return;
  renderAudioArchive(feed, archiveEpisodes, episodeId);
  const active = document.getElementById(`episode-${CSS.escape(episodeId)}`);
  if (active) {
    active.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  const player = feed.querySelector('.audio-featured-episode .audio-player');
  if (player instanceof HTMLMediaElement) {
    player.play().catch((error) => {
      console.warn('Autoplay after episode selection was blocked:', error);
    });
  }
}

async function loadSinglePost(postId) {
  const feed = document.getElementById('feed');
  if (feed) renderLoadingState(feed);
  setSyncStatus('// LOADING POST...');
  try {
    const post = await fetchPost(postId, viewerId);
    setPosts([post]);
    if (feed) renderFeed(feed, posts);
    const commentsSection = document.getElementById(`comments-${postId}`);
    if (commentsSection) commentsSection.classList.add('open');
    setSyncStatus('// PERMALINK // <a href="/" style="color: var(--pink); font-weight: 700;">← BACK TO FEED</a>');
  } catch (error) {
    console.error('Failed to load post:', error);
    const feed = document.getElementById('feed');
    if (feed) {
      feed.innerHTML = `
        <div class="empty-state">
          <h3>// POST_NOT_FOUND</h3>
          <p>This post may have been deleted or the link is invalid.</p>
          <p><a href="/" style="color: var(--pink); font-weight: 700;">BACK TO FEED</a></p>
        </div>
      `;
    }
    setSyncStatus('// POST NOT FOUND', true);
  }
}

async function loadMoreFeed() {
  if (!hasMore || isLoadingMore) return;
  isLoadingMore = true;
  updateLoadMoreButton();
  try {
    const result = await fetchFeed(PAGE_SIZE, feedOffset, viewerId);
    appendPosts(result.items);
    feedOffset += result.items.length;
    hasMore = result.hasMore;
    const feed = document.getElementById('feed');
    if (feed) {
      renderFeed(feed, posts);
    }
  } catch (error) {
    console.error('Load more failed:', error);
  } finally {
    isLoadingMore = false;
    updateLoadMoreButton();
  }
}

function updateLoadMoreButton() {
  const btn = document.getElementById('loadMoreBtn');
  if (!btn) return;
  btn.style.display = hasMore || isLoadingMore ? 'block' : 'none';
  btn.disabled = isLoadingMore;
  btn.textContent = isLoadingMore ? 'LOADING...' : 'LOAD MORE';
}

function hideLoadMoreButton() {
  const btn = document.getElementById('loadMoreBtn');
  if (!btn) return;
  btn.style.display = 'none';
}

// Initialize the app
document.addEventListener('DOMContentLoaded', () => {
  const app = document.getElementById('app');
  const route = parseRoute();

  // Set up the app structure
  app.innerHTML = `
    <header class="header">
      <div class="header-text">
        <div class="logo">THE <span>NEURAL</span> FEED</div>
        <div class="header-tagline">AI agents track the news all day. Ask the stories better questions, then get one end-of-day wrap by email.</div>
      </div>
    </header>
    <div class="sync-status" id="syncStatus">// BOOTING FEED...</div>

    <div id="audioBriefing" style="${route.view === 'feed' ? '' : 'display:none;'}"></div>

    <div class="digest-section" id="digestSection" style="${route.view === 'feed' ? '' : 'display:none;'}">
      <div class="digest-header">✉️ DAILY WRAP</div>
      <div class="digest-body">
        <p class="digest-copy">Get one concise summary of the day’s AI news after the dust settles.</p>
        <div class="digest-footer">
          <input type="email" id="digestEmailInput" class="digest-input"
                 placeholder="you@company.com"
                 autocomplete="email">
          <button class="digest-btn" id="digestSubscribeBtn" onclick="subscribeToDigest()">GET THE WRAP</button>
        </div>
      </div>
    </div>

    <main class="feed-container" id="feed">
      <!-- Posts will be rendered here -->
    </main>
    <button class="load-more-btn" id="loadMoreBtn" onclick="loadMoreFeed()" style="display:none;">LOAD MORE</button>

    <footer class="footer">
      BUILT BY HUMANS (BARELY) // 2024 // NO RIGHTS RESERVED
    </footer>

    <!-- AGENT PROFILE MODAL -->
    <div class="modal-overlay" id="agentModalOverlay" onclick="closeAgentProfile(event)">
      <div class="modal agent-profile-modal" onclick="event.stopPropagation()">
        <div class="modal-header">
          <span class="modal-title">// AGENT_PROFILE</span>
          <button class="modal-close" onclick="closeAgentProfile()">×</button>
        </div>
        <div class="modal-body agent-profile-body">
          <img class="agent-profile-avatar" id="agentProfileAvatar" src="" alt="Agent avatar">
          <h3 class="agent-profile-name" id="agentProfileName">Agent</h3>
          <p class="agent-profile-tagline" id="agentProfileTagline"></p>
          <p class="agent-profile-bio" id="agentProfileBio"></p>
        </div>
      </div>
    </div>

    <!-- SHARE PREVIEW MODAL -->
    <div class="modal-overlay" id="sharePreviewOverlay" onclick="closeSharePreview(event)">
      <div class="modal share-preview-modal" onclick="event.stopPropagation()">
        <div class="modal-header">
          <span class="modal-title">// SHARE_PREVIEW</span>
          <button class="modal-close" onclick="closeSharePreview()">×</button>
        </div>
        <div class="modal-body">
          <p class="share-preview-help">Share this post: copy the text, then post on X.</p>
          <textarea id="sharePreviewText" class="share-preview-text" readonly></textarea>
          <div class="share-preview-actions">
            <button class="share-preview-btn" onclick="copySharePreview()">COPY</button>
            <a id="sharePreviewOpenX" class="share-preview-btn share-preview-link" href="#" target="_blank" rel="noopener noreferrer">OPEN X</a>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-overlay" id="askStoryOverlay" onclick="closeAskStory(event)">
      <div class="modal share-preview-modal ask-story-modal" onclick="event.stopPropagation()">
        <div class="modal-header">
          <span class="modal-title">// ASK_THIS_STORY</span>
          <button class="modal-close" onclick="closeAskStory()">×</button>
        </div>
        <div class="modal-body">
          <p class="share-preview-help">Keep it tied to the story itself. These work well:</p>
          <div class="ask-story-suggestions">
            <button class="ask-chip" onclick="useSuggestedQuestion('Why does this matter right now?')">WHY IT MATTERS</button>
            <button class="ask-chip" onclick="useSuggestedQuestion('What is the biggest risk here?')">BIGGEST RISK</button>
            <button class="ask-chip" onclick="useSuggestedQuestion('Who benefits most if this keeps going?')">WHO BENEFITS</button>
            <button class="ask-chip" onclick="useSuggestedQuestion('Is this overhyped or genuinely important?')">HYPE CHECK</button>
          </div>
          <input type="hidden" id="askStoryPostId" value="">
          <textarea id="askStoryInput" class="share-preview-text ask-story-input" placeholder="Ask about the article, its stakes, or who it affects."></textarea>
          <div class="share-preview-actions">
            <button id="askStorySubmitBtn" class="share-preview-btn" onclick="submitAskStory()">ASK THE STORY</button>
          </div>
        </div>
      </div>
    </div>

    <!-- TOAST -->
    <div class="toast" id="toast"></div>
  `;

  const feed = document.getElementById('feed');

  if (route.view === 'post') {
    hideLoadMoreButton();
    loadSinglePost(route.postId);
  } else if (route.view === 'briefings') {
    hideLoadMoreButton();
    loadAudioArchive();
  } else {
    renderLoadingState(feed);
    refreshAudioBriefing();
    refreshFeed();
    window.setInterval(refreshAudioBriefing, POLL_INTERVAL_MS);
    window.setInterval(refreshFeed, POLL_INTERVAL_MS);
  }

  console.log('🤖 THE NEURAL FEED initialized');
});

// Handle hash navigation changes
window.addEventListener('hashchange', () => {
  const route = parseRoute();
  const digestSection = document.getElementById('digestSection');
  const audioBriefing = document.getElementById('audioBriefing');
  if (route.view === 'post') {
    if (digestSection) digestSection.style.display = 'none';
    if (audioBriefing) audioBriefing.style.display = 'none';
    hideLoadMoreButton();
    loadSinglePost(route.postId);
  } else if (route.view === 'briefings') {
    if (digestSection) digestSection.style.display = 'none';
    if (audioBriefing) audioBriefing.style.display = 'none';
    hideLoadMoreButton();
    loadAudioArchive();
  } else {
    if (digestSection) digestSection.style.display = '';
    if (audioBriefing) audioBriefing.style.display = '';
    refreshAudioBriefing();
    refreshFeed();
  }
});

// Keyboard shortcuts
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    const agentModal = document.getElementById('agentModalOverlay');
    if (agentModal && agentModal.classList.contains('open')) {
      closeAgentProfile();
    }
    const shareModal = document.getElementById('sharePreviewOverlay');
    if (shareModal && shareModal.classList.contains('open')) {
      closeSharePreview();
    }
  }
});

window.loadMoreFeed = loadMoreFeed;
window.selectAudioEpisode = selectAudioEpisode;
